import React, { useEffect, useState } from 'react';
import { Modal } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import ModalEditBody from './ModalEditBody';
import ModalEditBodyDelete from './ModalEditBodyDelete';
import ModalEditFooter from '../../common/ModalEditFooter';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, createOrganizationBase, deleteOrganizationBase, updateOrganizationBase } from '../../../redux/slices/organizationBase';
import { EDIT_ACTION } from '../../../constants';
import type { CreateOrganizationBaseParams, DeleteOrganizationBaseParams, OrganizationBaseType, UpdateOrganizationBaseParamsClient } from '../../../types/organizationBase';

const ModalEditOrganization = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dialogMode, isDialogOpen, organizationBase, organizationBases } = useSelector((state: RootState) => state.organizationBase);
  const [organizationBaseName, setOrganizationBaseName] = useState<string>('');
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [buttonLabel, setButtonLabel] = useState<string>('');
  const [deleteFlag, setDeleteFlag] = useState<boolean>(false);
  const [isUniqueName, setIsUniqueName] = useState<boolean | undefined>(undefined);

  // すべて入力されているかどうか（OKボタンを押せるかどうかに使う）
  const isFulfilled: boolean = organizationBaseName !== '';

  // SIDE EFFECT
  // ================================================================================

  /**
   * 確認用
   */
  // useEffect(() => {
  // }, []);

  /**
   * 新規作成モード以外では初期値を入れておく
   */
  useEffect(() => {
    if (dialogMode !== EDIT_ACTION.CREATE && organizationBase) {
      setOrganizationBaseName(organizationBase.organizationBaseName);
    }
  }, [organizationBase]);

  /**
   * モードに併せてタイトルとボタン名を設定
   */
  useEffect(() => {
    if (dialogMode === EDIT_ACTION.CREATE) {
      setDialogTitle(t('organizationBase.dialog.titleCreate'));
      setButtonLabel(t('dialog.create'));
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      setDialogTitle(t('organizationBase.dialog.titleUpdate'));
      setButtonLabel(t('dialog.update'));
    } else if (dialogMode === EDIT_ACTION.DELETE) {
      setDialogTitle(t('organizationBase.dialog.titleDelete'));
      setButtonLabel(t('dialog.delete'));
    } else {
      setDialogTitle('');
      setButtonLabel('');
    }
  }, [dialogMode]);

  /**
   * 組織名の重複チェック
   */
  useEffect(() => {
    // console.log(organizationBaseName, dialogMode);
    // 新規作成モードではorganizationBasesに同じ名前のものがないかチェック
    if (dialogMode === EDIT_ACTION.CREATE) {
      const sameOrganizationBaseName: OrganizationBaseType | undefined = organizationBases ? organizationBases.find((base) => base.organizationBaseName === organizationBaseName) : undefined;
      if (sameOrganizationBaseName !== undefined) {
        setIsUniqueName(false);
      } else {
        setIsUniqueName(true);
      }
      // 更新モードでは自分以外のorganizationBasesに同じ名前のものがないかチェック
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      const sameOrganizationBaseName: OrganizationBaseType | undefined = organizationBases.find((base) => {
        if (base.id !== organizationBase.id && base.organizationBaseName === organizationBaseName) {
          return true;
        } else {
          return false;
        }
      });
      if (sameOrganizationBaseName !== undefined) {
        setIsUniqueName(false);
      } else {
        setIsUniqueName(true);
      }
      // 削除モードでは何もしない
    } else if (dialogMode === EDIT_ACTION.DELETE) {
      setIsUniqueName(undefined);
    } else {
      setIsUniqueName(undefined);
    }
  }, [organizationBaseName]);

  // EVENT HANDLER
  // ================================================================================

  const resetModal = (): void => {
    dispatch(closeDialog());
    setOrganizationBaseName('');
    setDeleteFlag(false);
    setIsUniqueName(undefined);
  };

  /**
   * 組織名を変更した時
   */
  const handleOrganizationBaseNameChange = (event: React.ChangeEvent<{ value: string }>): void => {
    // console.log(event);
    const newOrganizationBaseName: string = event.target.value;
    setOrganizationBaseName(newOrganizationBaseName);
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleSave = (): void => {
    // 新規作成時
    if (dialogMode === EDIT_ACTION.CREATE) {
      const params: CreateOrganizationBaseParams = {
        organizationBaseName: organizationBaseName,
      };
      dispatch(createOrganizationBase(params));

      // 更新時
    } else if (dialogMode === EDIT_ACTION.UPDATE && organizationBase) {
      const params: UpdateOrganizationBaseParamsClient = {
        id: organizationBase.id,
        organizationBaseName: organizationBaseName,
      };
      dispatch(updateOrganizationBase(params));

      // 削除時
    } else if (dialogMode === EDIT_ACTION.DELETE && organizationBase) {
      const params: DeleteOrganizationBaseParams = {
        id: organizationBase.id,
      };
      dispatch(deleteOrganizationBase(params));

      // その他
    } else {
      // do nothing
    }
    resetModal();
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    setDeleteFlag(!deleteFlag);
  };

  // VIEW
  // ================================================================================

  return (
    <Modal id="modalEditOrganization" open={isDialogOpen} maxWidth={'md'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'corporate_fare'} title={dialogTitle} />
      {/* 新規作成時・更新時 */}
      {(dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.UPDATE) && (
        <ModalEditBody
          label={t('organizationBase.organizationBase')}
          value={organizationBaseName}
          invalid={isUniqueName === false}
          invalidText={t('dialog.errorDup')}
          onChange={handleOrganizationBaseNameChange}
        />
      )}

      {/* 削除時 */}
      {dialogMode === EDIT_ACTION.DELETE && <ModalEditBodyDelete description={t('organizationBase.dialog.card1')} label={t('organizationBase.dialog.card2')} name={organizationBaseName} />}

      <ModalEditFooter
        mode={dialogMode}
        theme={dialogMode === EDIT_ACTION.DELETE ? 'danger' : 'primary'}
        disabled={(dialogMode === EDIT_ACTION.CREATE && !isUniqueName) || (dialogMode === EDIT_ACTION.UPDATE && !isUniqueName) || (dialogMode === EDIT_ACTION.DELETE && !deleteFlag) || !isFulfilled}
        labelCancel={t('dialog.cancel')}
        labelSave={buttonLabel}
        labelCheckBox={t('dialog.validDelete')}
        deleteFlag={deleteFlag}
        onCancel={handleCancel}
        onSave={handleSave}
        onToggle={handleToggleDelete}
      />
    </Modal>
  );
};

export default ModalEditOrganization;
